import { Box, Stack, Tab, Tabs } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { Outlet, useNavigate, Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomNavLink from "../../components/Links/CustomNavLink";
import { Button, Menu, MenuItem } from "@mui/material";
import styles from "./Home.module.css";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

/**
 * Language options shown in the top-right language switcher.
 * Adding a new language here is enough to make it appear in the UI;
 * the route and i18n support must also be configured separately.
 */
const availableLanguages = [{code: "en", label: "English"}, {code: "ru", label: "Russian"}, {code: "uk", label: "Ukrainian"}];

/**
 * Navigation shell for all main pages (non-widget routes).
 *
 * Renders a top bar with:
 *  - "Home" link
 *  - "Tolerance" dropdown menu leading to each calculator
 *  - Language switcher that rewrites the current URL's language prefix
 *
 * The language switcher preserves the current page path — e.g. switching from
 * English to Ukrainian while on `/en/tolerance/fan` navigates to `/uk/tolerance/fan`.
 * This is done by `transformLocation`, which replaces only the language segment.
 *
 * All navigation links are built with the current language prefix via
 * `transformPath` so that internal links never strip the active language.
 */
const HomeLayout = () => {

  const { t, i18n } = useTranslation();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const location = useLocation();

  /**
   * Replaces the language segment of the current URL with the given code.
   * Keeps the rest of the path intact so the user stays on the same page.
   */
  const transformLocation = useCallback((code) => {
    const pathSegments = location.pathname.split('/');
    return pathSegments.slice(0, 1).join('/') + `/${code}/` + pathSegments.slice(2).join('/');
  }, [i18n, location.pathname]);

  /**
   * Prepends the active language code to an app-relative path.
   * Used for internal nav links so they always carry the current language.
   */
  const transformPath = useCallback((path) => {
    return `/${i18n.language}` + path;
  }, [i18n]);

  const languageSwitchers = useMemo(() => {
    return availableLanguages.map((lang) => {
      const path = transformLocation(lang.code);
      return <CustomNavLink key={lang.code} to={path}>
        {lang.label}
      </CustomNavLink>
    });
  }, [transformLocation]);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1}>
            <CustomNavLink to={transformPath('/')}>Home</CustomNavLink>
            <Button
              id="basic-button"
              aria-controls={open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
              color="black"
              endIcon={<KeyboardArrowDownIcon />}
            >
              Tolerance
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem onClick={handleClose}>
                <NavLink
                  className={styles.link}
                  to={transformPath('/tolerance/general')}
                >
                  General
                </NavLink>
              </MenuItem>
              <MenuItem onClick={handleClose}>
                <NavLink
                  className={styles.link}
                  to={transformPath('/tolerance/fan')}
                >
                  Fan
                </NavLink>
              </MenuItem>
              <MenuItem onClick={handleClose}>
                <NavLink
                  className={styles.link}
                  to={transformPath('/tolerance/driveshaft')}
                >
                  Driveshaft
                </NavLink>
              </MenuItem>
            </Menu>
          </Stack>
          <Stack direction="row" spacing={1}>
            {languageSwitchers}
          </Stack>
        </Stack>
      </Box>
      <Outlet />
    </Box>
  );
};

export default HomeLayout;
